import { ApiRequestError } from '@/api/client';
import { envelopeMessage } from '@/api/envelope';
import { http } from '@/api/http';

export type TeamEmailTemplate = {
  id: number;
  key: string;
  label: string;
  description: string;
  /** Empty when this item is using the default text. */
  customValue: string;
  defaultValue: string;
};

const TEMPLATE_LABELS: Record<string, string> = {
  order_confirmation_greeting: 'Order confirmation greeting',
  invoice_email_greeting: 'Invoice email greeting',
  greeting: 'Greeting',
};

const LIST_KEYS = ['list', 'items', 'records', 'rows', 'templates', 'configs', 'data'] as const;
const ID_KEYS = ['config_id', 'id', 'template_id'] as const;
const KEY_KEYS = ['config_key', 'template_key', 's_key', 'key', 'code', 'field'] as const;
const NAME_KEYS = ['config_name', 'template_name', 'name', 'title', 'label'] as const;
const DESCRIPTION_KEYS = ['description', 'remark', 'hint', 'summary'] as const;
const DEFAULT_KEYS = ['default_value', 'default_content', 'default_text', 'default', 'default_greeting'] as const;
const CUSTOM_KEYS = ['custom_value', 'custom_content', 'custom_text'] as const;
const EFFECTIVE_KEYS = ['value', 'content', 's_value', 'current_value', 'effective_value'] as const;

type HttpEnvelope = {
  error?: boolean;
  message?: string;
  data?: unknown;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function readNumber(record: Record<string, unknown>, keys: readonly string[]): number | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
      return Number(value);
    }
  }
  return undefined;
}

function readString(record: Record<string, unknown>, keys: readonly string[]): string {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return '';
}

function readPresentString(
  record: Record<string, unknown>,
  keys: readonly string[],
): { present: boolean; value: string } {
  for (const key of keys) {
    if (!(key in record)) continue;
    const value = record[key];
    if (typeof value === 'string') return { present: true, value: value.trim() };
    if (value == null) return { present: true, value: '' };
  }
  return { present: false, value: '' };
}

function readBoolean(record: Record<string, unknown>, keys: readonly string[]): boolean | undefined {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'boolean') return value;
    if (value === 1 || value === '1' || value === 'true') return true;
    if (value === 0 || value === '0' || value === 'false') return false;
  }
  return undefined;
}

function isEnglishText(value: string): boolean {
  return value.length > 0 && !/[\u3400-\u9fff]/.test(value);
}

function humanizeKey(key: string): string {
  const text = key.replace(/[_-]+/g, ' ').trim();
  if (!text) return '';
  return text.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function templateLabel(key: string, name: string, id: number): string {
  if (TEMPLATE_LABELS[key]) return TEMPLATE_LABELS[key];
  if (isEnglishText(name)) return name;
  const humanized = humanizeKey(key);
  if (humanized) return humanized;
  return `Email template ${id}`;
}

function parseTeamEmailTemplate(row: unknown): TeamEmailTemplate | null {
  const record = asRecord(row);
  if (!record) return null;
  const id = readNumber(record, ID_KEYS);
  if (id == null || !Number.isInteger(id)) return null;

  const key = readString(record, KEY_KEYS);
  const name = readString(record, NAME_KEYS);
  const description = readString(record, DESCRIPTION_KEYS);
  const defaultValue = readString(record, DEFAULT_KEYS);
  const custom = readPresentString(record, CUSTOM_KEYS);
  const effective = readPresentString(record, EFFECTIVE_KEYS);
  const customized = readBoolean(record, ['is_customized', 'is_custom', 'customized']);
  const usingDefault = readBoolean(record, ['is_default', 'using_default']);

  let customValue = '';
  if (customized === false || usingDefault === true) {
    customValue = '';
  } else if (customized === true) {
    customValue = custom.present ? custom.value : effective.value;
  } else if (custom.present) {
    customValue = custom.value;
  } else if (effective.present && effective.value !== defaultValue) {
    customValue = effective.value;
  }
  if (defaultValue && customValue === defaultValue) customValue = '';

  return {
    id,
    key,
    label: templateLabel(key, name, id),
    description: isEnglishText(description) && description !== templateLabel(key, name, id) ? description : '',
    customValue,
    defaultValue,
  };
}

function rowsFromPayload(payload: unknown): unknown[] {
  if (Array.isArray(payload)) return payload;
  const record = asRecord(payload);
  if (!record) return [];

  const candidates: unknown[][] = [];
  for (const key of LIST_KEYS) {
    if (Array.isArray(record[key])) candidates.push(record[key] as unknown[]);
  }
  if (candidates.length === 0) {
    for (const value of Object.values(record)) {
      if (Array.isArray(value)) candidates.push(value);
    }
  }
  if (candidates.length > 0) {
    return candidates
      .map((rows) => ({ rows, score: rows.filter((row) => parseTeamEmailTemplate(row)).length }))
      .sort((a, b) => b.score - a.score)[0].rows;
  }

  const nested = asRecord(record.data);
  if (nested && nested !== record) return rowsFromPayload(nested);
  if (parseTeamEmailTemplate(record)) return [record];
  return [];
}

/** 从 `POST /system/team/order/email_template/list` 的 `data` 读取模板项。 */
export function parseTeamEmailTemplates(data: unknown): TeamEmailTemplate[] {
  const seen = new Set<number>();
  const items: TeamEmailTemplate[] = [];
  for (const row of rowsFromPayload(data)) {
    const item = parseTeamEmailTemplate(row);
    if (!item || seen.has(item.id)) continue;
    seen.add(item.id);
    items.push(item);
  }
  return items;
}

/** POST `/system/team/order/email_template/list` — 返回全部配置，未改过的项带默认值。 */
export async function fetchTeamEmailTemplates(): Promise<TeamEmailTemplate[]> {
  const res = await http.post<HttpEnvelope>(
    '/system/team/order/email_template/list',
    undefined,
    { skipApiEnvelopeModal: true },
  );
  if (res.error === true) {
    throw new ApiRequestError(
      typeof res.message === 'string' ? res.message : 'Failed to load email templates.',
    );
  }
  return parseTeamEmailTemplates(res.data);
}

function templateFailureMessage(message: string, fallback: string): string {
  const text = message.trim();
  if (!text) return fallback;
  if (text === '无权限操作') return 'You do not have permission to change email templates.';
  return text;
}

function readThrownMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiRequestError) return templateFailureMessage(error.message, fallback);
  if (!error || typeof error !== 'object' || !('response' in error)) return fallback;
  const data = (error as { response?: { data?: unknown } }).response?.data;
  if (typeof data === 'string' && data.trim()) return data.trim();
  if (!data || typeof data !== 'object') return fallback;
  const record = data as Record<string, unknown>;
  if (typeof record.message === 'string') return templateFailureMessage(record.message, fallback);
  const detail = record.detail;
  if (typeof detail === 'string') return templateFailureMessage(detail, fallback);
  if (Array.isArray(detail)) {
    const parts = detail
      .map((item) => {
        if (typeof item === 'string') return item.trim();
        if (item && typeof item === 'object' && typeof (item as { msg?: unknown }).msg === 'string') {
          return (item as { msg: string }).msg.trim();
        }
        return '';
      })
      .filter(Boolean);
    if (parts.length > 0) return parts.join(' ');
  }
  return fallback;
}

/**
 * POST `/system/team/order/email_template/set`。
 * `customValue` 为空时恢复该项默认文案。
 */
export async function postSetTeamEmailTemplate(
  configId: number,
  customValue: string,
): Promise<{ error: boolean; message: string }> {
  const failFallback = 'Failed to save the email template.';
  const okFallback = 'Email template saved.';
  try {
    const res = await http.post<HttpEnvelope>(
      '/system/team/order/email_template/set',
      { config_id: configId, custom_value: customValue },
      { skipApiEnvelope: true },
    );
    if (res.error === true) {
      return {
        error: true,
        message: templateFailureMessage(envelopeMessage(res, failFallback), failFallback),
      };
    }
    return { error: false, message: envelopeMessage(res, okFallback) };
  } catch (error) {
    return { error: true, message: readThrownMessage(error, failFallback) };
  }
}
